import { EApplicantType, EFinancingProgram } from '@if/core/types'
import { ActionButton } from '@if/mui'
import { useContinueWithComputedIncome } from '@src/data/api/credit-api/credit-api'
import { ApplicantDto } from '@src/types/CreditApplicationSchema'
import { useTranslation } from 'react-i18next'
import ResetBankAccountButton from './ResetBankAccountButton'
import { useCanContinueWithComputedIncome } from './incomeVerification-hooks'

interface Props {
  applicant: ApplicantDto
  hasIncomeDocumentsPendingUpload: boolean
  hasIncomeDocumentsPendingReview: boolean
  hasIncomeServiceAdjustment: boolean
  creditApplicationId: string
  financingProgramId: EFinancingProgram
  canEdit: boolean
}

const IncomeVerificationMismatch = ({
  applicant,
  hasIncomeDocumentsPendingUpload,
  hasIncomeDocumentsPendingReview,
  hasIncomeServiceAdjustment,
  creditApplicationId,
  financingProgramId,
  canEdit,
}: Props) => {
  const { t } = useTranslation()
  const [continueWithComputedIncome, isContinuingWithComputedIncome] = useContinueWithComputedIncome()

  const applicantType = applicant.isPrimaryApplicant ? EApplicantType.Applicant : EApplicantType.CoApplicant

  const canContinueWithComputedIncome = useCanContinueWithComputedIncome(applicant)

  const onContinueWithComputedIncome = async () => {
    await continueWithComputedIncome({
      creditApplicationParams: { creditApplicationId, financingProgramId },
      applicantType,
    })
  }
  const incomeMismatchUnderReview =
    (hasIncomeDocumentsPendingReview || !applicant.incomeVerification?.allIncomeTasksConfirmed) &&
    !hasIncomeDocumentsPendingUpload

  return (
    <div>
      {incomeMismatchUnderReview && (
        <>
          {!hasIncomeServiceAdjustment && <p>{t('incomeVerification.incomeMismatch.noMatch')}</p>}
          <p>{t('incomeVerification.startVerification.reviewing')}</p>
          <p>{t('incomeVerification.startVerification.notified')}</p>
        </>
      )}

      {hasIncomeDocumentsPendingUpload && (
        <>
          <p>{t('incomeVerification.incomeMismatch.noMatchProvideDocs')}</p>
          {canContinueWithComputedIncome && <p>{t('incomeVerification.incomeMismatch.continueOrSubmit')}</p>}
        </>
      )}
      {!hasIncomeServiceAdjustment && canContinueWithComputedIncome && canEdit && (
        <ActionButton
          fullWidth
          emphasis="principal"
          onClick={onContinueWithComputedIncome}
          loading={isContinuingWithComputedIncome}
        >
          {t('incomeVerification.incomeMismatch.continue')}
        </ActionButton>
      )}
      {!hasIncomeServiceAdjustment && canEdit && (
        <>
          <p>{t('incomeVerification.nameMismatch.reset')}</p>
          <ResetBankAccountButton
            applicantType={applicantType}
            id={creditApplicationId}
            financingProgramId={financingProgramId}
          />
        </>
      )}
    </div>
  )
}

export default IncomeVerificationMismatch
